import React from 'react';
import { Input } from './Input';

/**
 * NameFields — canonical first/last name input pair.
 *
 * DEC-43 (2026-07-28): all customer-facing name inputs split into first + last.
 * DEC-54 (2026-08-01): split everywhere, join on submit, never derive by splitting.
 *
 * Shape: two inputs side by side in a responsive grid.
 *   [First name]  [Last name]
 *
 * Labels: sentence case ("First name" / "Last name"), matching the app's
 * existing convention ("Company name (optional)", "ZIP code", "Street address").
 *
 * Consumers (3, registered in COMPONENT_MAP.md):
 *   1. AddUserModal (UsersRolesPage) — join to single `name` on submit (backend
 *      stores one string; split is UI-only per DEC-54)
 *   2. AddCustomerDrawer — writes first_name + last_name to customer record
 *   3. CustomerFormPanel (quote builder) — was single "Customer name" with
 *      .split(' ') derivation (DEC-37 function defect); now split inputs
 *
 * NOT used for contact rows (AddCustomerDrawer / CustomerFormPanel inline
 * contacts) — those have a different control pattern (array-indexed updater,
 * no visible label, different gap, surrounding primary-radio + "Same as
 * customer name" checkbox). Registered exclusion in COMPONENT_MAP with trigger:
 * revisit if contact rows gain visible labels or a third mount appears.
 *
 * Props:
 *   firstName       string — controlled value
 *   lastName        string — controlled value
 *   onFirstNameChange  (e: ChangeEvent) => void — standard input onChange
 *   onLastNameChange   (e: ChangeEvent) => void — standard input onChange
 *   required        boolean (default true) — applies to both fields
 *   disabled        boolean (default false)
 *   firstRef        React ref — forwarded to the first input (for autofocus)
 *   idPrefix        string (default 'name') — generates id="<prefix>-first" / id="<prefix>-last"
 *   className       string — applied to the outer grid wrapper
 */
export default function NameFields({
  firstName,
  lastName,
  onFirstNameChange,
  onLastNameChange,
  required = true,
  disabled = false,
  firstRef,
  idPrefix = 'name',
  className,
}) {
  return (
    <div className={`grid grid-cols-2 gap-3${className ? ` ${className}` : ''}`}>
      <div>
        <label
          htmlFor={`${idPrefix}-first`}
          className="block text-xs font-medium mb-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          First name
        </label>
        <Input
          id={`${idPrefix}-first`}
          ref={firstRef}
          value={firstName}
          onChange={onFirstNameChange}
          placeholder="First name"
          required={required}
          disabled={disabled}
        />
      </div>
      <div>
        <label
          htmlFor={`${idPrefix}-last`}
          className="block text-xs font-medium mb-1"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Last name
        </label>
        <Input
          id={`${idPrefix}-last`}
          value={lastName}
          onChange={onLastNameChange}
          placeholder="Last name"
          required={required}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
